import ArrowDown from '@sats-group/icons/24/arrow-down';
import Mail from '@sats-group/icons/24/mail';
import Button from '@sats-group/ui-lib/react/button';
import Collapse from '@sats-group/ui-lib/react/collapse/collapse';
import HiddenInput from '@sats-group/ui-lib/react/hidden-input';
import useToggle from '@sats-group/ui-lib/react/hooks/use-toggle';
import LinkButton from '@sats-group/ui-lib/react/link-button';
import Text from '@sats-group/ui-lib/react/text';
import TextArea from '@sats-group/ui-lib/react/text-area';
import TextInput from '@sats-group/ui-lib/react/text-input';
import React, { useState } from 'react';
import { mod } from '~/client/helpers/add-bem-modifiers';
import InputButton from '../input-button/input-button';
import RadioList from '../radio-list/radio-list';
import type { ContactForm as Props } from './contact-option.types';

const ContactOption: React.FunctionComponent<Props> = ({
  action,
  body,
  cancel,
  description,
  files,
  hiddenInputs,
  open = true,
  radioInputs,
  submit,
  textInputs,
  title,
  trigger,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, toggle] = useToggle(open);

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).map(file => file.name);
      setUploadedFiles([...files]);
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = event => {
    if (isLoading || !(event.target as HTMLFormElement).checkValidity()) {
      event.preventDefault();
      return;
    }
    setIsLoading(true);
  };

  return (
    <div className="contact-option">
      <Text size={Text.sizes.large} theme={Text.themes.emphasis}>
        {title}
      </Text>
      <div className="contact-option__collapse">
        <button
          className="contact-option__trigger"
          data-testid="open-form"
          onClick={toggle}
        >
          <div>
            <Mail />
          </div>
          <Text size={Text.sizes.basic} theme={Text.themes.emphasis}>
            {trigger}
          </Text>
          <div
            className={mod('contact-option__trigger-icon', {
              flipped: isOpen,
            })}
          >
            <ArrowDown />
          </div>
        </button>
        <Collapse isOpen={isOpen}>
          <div className="contact-option__hideable">
            <Text size={Text.sizes.small}>{description}</Text>
            <form
              action={action}
              className="contact-option__form"
              data-testid="form"
              method="post"
              onSubmit={handleSubmit}
              encType="multipart/form-data"
            >
              {hiddenInputs.map(input => (
                <HiddenInput {...input} key={input.name} />
              ))}
              <div className="contact-option__inputs">
                {radioInputs.map(entry => (
                  <RadioList
                    {...entry}
                    key={entry.label}
                    showOnlyOneCustomError
                  />
                ))}
              </div>
              <TextArea {...body} required />
              <div className="contact-option__inputs contact-option__inputs--split">
                {textInputs.map(input => (
                  <TextInput {...input} key={input.name} />
                ))}
              </div>
              <div className="contact-option__attachments">
                <Text theme={Text.themes.emphasis}>{files.title}</Text>
                {files.description ? (
                  <Text elementName="p">{files.description}</Text>
                ) : null}
                <div className="contact-option__attachments-inputs">
                  <div>
                    <InputButton
                      {...files.inputButton.button}
                      type="file"
                      onChange={handleUpload}
                      multiple
                      required={files.required}
                    >
                      {files.inputButton.label}
                    </InputButton>
                  </div>

                  {uploadedFiles.length > 0 ? (
                    <div>
                      {uploadedFiles.map(file => (
                        <Text
                          className="contact-option__attachment-name"
                          key={file}
                        >
                          {file}
                        </Text>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="contact-option__form-actions">
                <LinkButton
                  {...cancel}
                  variant={LinkButton.variants.secondary}
                />
                <div>
                  <Button
                    disabled={isLoading}
                    spinning={isLoading}
                    text={submit}
                    type="submit"
                    variant={Button.variants.cta}
                  />
                </div>
              </div>
            </form>
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default ContactOption;
